import { ActivityTimeline, Button, DetailList, formatDate, ListView, RecordDrawer, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { type DropdownItem, DropdownMenu } from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useActivity } from "../../activity";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import {
  fleetActions,
  ownerQuery,
  TRUCK_STATUS,
  type Truck,
  type TruckFields,
  type TruckStatus,
  truckQuery,
  useRefreshFleet,
} from "../../fleet";
import { ConfirmDialog } from "../shared/confirm";
import { OwnerFilter, SearchBox, useSearchText } from "./filters";
import styles from "./fleet.module.css";
import { type OwnerChoice, OwnerSelect, ownerName } from "./owner-select";
import type { FleetCan, FleetSearch } from "./page";

const STATUSES = Object.keys(TRUCK_STATUS) as TruckStatus[];
const litres = (n: number) => `${n.toLocaleString("en-KE")} L`;

/** Trucks, newest first, by part of the plate and by status. `?truck=` opens one beside the list. */
export function TrucksView({
  search,
  set,
  can,
  adding,
  onAdded,
}: {
  search: FleetSearch;
  set: (patch: Partial<FleetSearch>, replace?: boolean) => void;
  can: FleetCan;
  adding: boolean;
  onAdded: () => void;
}) {
  const format = useCompanyFormat();
  const text = useSearchText(search.q, set);
  const [dialog, setDialog] = useState<{ kind: "edit" | "retire"; truck: Truck } | null>(null);
  const list = useCursorList({
    queryKey: ["fleet", "trucks", "list", search.q ?? "", search.status ?? "", search.owner ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/fleet/trucks", {
        params: { query: { q: search.q, status: search.status, ...ownerQuery(search.owner), cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });

  return (
    <>
      <ListView
        caption="Trucks"
        itemName={{ one: "truck", other: "trucks" }}
        rows={list.rows}
        rowKey={(t) => t.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(search.q || search.status || search.owner)}
        onResetFilters={() => {
          text.clear();
          set({ q: undefined, status: undefined, owner: undefined });
        }}
        empty={{
          title: "No trucks yet",
          description: "Add your trucks, and partners add theirs in the partner portal.",
        }}
        toolbar={
          <div className={styles.toolbarEnd}>
            <SearchBox label="Search trucks" placeholder="Plate" text={text} />
            <Select
              label="Status"
              value={search.status ?? "any"}
              onValueChange={(v) => set({ status: v === "any" ? undefined : (v as TruckStatus) })}
              options={[
                { value: "any", label: "Any status" },
                ...STATUSES.map((s) => ({ value: s, label: TRUCK_STATUS[s][0] })),
              ]}
            />
            <OwnerFilter value={search.owner} onChange={(owner) => set({ owner })} partners={can.partners} />
          </div>
        }
        columns={[
          {
            key: "plate_number",
            label: "Plate",
            render: (t) => (
              <button type="button" className={styles.link} onClick={() => set({ truck: t.id })}>
                {t.plate_number}
              </button>
            ),
          },
          {
            key: "fleet_number",
            label: "Fleet no.",
            render: (t) => (t.fleet_number === null ? "—" : String(t.fleet_number)),
          },
          { key: "capacity_litres", label: "Capacity", numeric: true, render: (t) => litres(t.capacity_litres) },
          { key: "make", label: "Make and model", render: (t) => [t.make, t.model].filter(Boolean).join(" ") || "—" },
          { key: "partner_id", label: "Owner", render: (t) => ownerName(t) },
          {
            key: "status",
            label: "Status",
            render: (t) => <Badge tone={TRUCK_STATUS[t.status][1]}>{TRUCK_STATUS[t.status][0]}</Badge>,
          },
          {
            key: "actions",
            label: "",
            render: (t) =>
              can.manage ? (
                <DropdownMenu
                  label="Actions"
                  items={
                    [
                      { label: "Edit", onSelect: () => setDialog({ kind: "edit", truck: t }) },
                      { label: "Retire", destructive: true, onSelect: () => setDialog({ kind: "retire", truck: t }) },
                    ] satisfies DropdownItem[]
                  }
                />
              ) : null,
          },
        ]}
      />
      <p className={styles.hint}>
        A truck a driver is on shift with can't be retired. Close the shift on On shift first.
      </p>
      <TruckDrawer
        id={search.truck}
        canEdit={can.manage}
        onEdit={(truck) => setDialog({ kind: "edit", truck })}
        onClose={() => set({ truck: undefined })}
        format={format}
      />
      {adding ? <TruckDialog canChooseOwner={can.partners} onClose={onAdded} /> : null}
      {dialog?.kind === "edit" ? (
        <TruckDialog truck={dialog.truck} canChooseOwner={can.partners} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "retire" ? <RetireTruck truck={dialog.truck} onClose={() => setDialog(null)} /> : null}
    </>
  );
}

/** One truck beside the list: its details and history. Search results open trucks here. */
function TruckDrawer({
  id,
  canEdit,
  onEdit,
  onClose,
  format,
}: {
  id: string | undefined;
  canEdit: boolean;
  onEdit: (truck: Truck) => void;
  onClose: () => void;
  format: ReturnType<typeof useCompanyFormat>;
}) {
  const truck = useQuery({ ...truckQuery(id ?? ""), enabled: Boolean(id) });
  const activity = useActivity("truck", id ?? null);
  const t = truck.data;
  return (
    <RecordDrawer
      open={Boolean(id)}
      onOpenChange={(o) => !o && onClose()}
      title={t?.plate_number ?? "Truck"}
      description={t ? ownerName(t) : undefined}
    >
      {truck.error ? (
        <Alert tone="danger" title="Couldn't load the truck">
          {messageFor(errorOf(truck.error))}
        </Alert>
      ) : null}
      {t ? (
        <div className={styles.drawer}>
          <DetailList
            items={[
              { label: "Status", value: <Badge tone={TRUCK_STATUS[t.status][1]}>{TRUCK_STATUS[t.status][0]}</Badge> },
              { label: "Fleet number", value: t.fleet_number === null ? null : String(t.fleet_number) },
              { label: "Capacity", value: litres(t.capacity_litres) },
              { label: "Loads a day", value: String(t.loads_per_day) },
              { label: "Make and model", value: [t.make, t.model].filter(Boolean).join(" ") || null },
              { label: "Owner", value: ownerName(t) },
              { label: "Added", value: formatDate(t.created_at, format) },
            ]}
          />
          {canEdit ? (
            <div>
              <Button variant="secondary" onClick={() => onEdit(t)}>
                Edit
              </Button>
            </div>
          ) : null}
          {activity.data ? <ActivityTimeline entries={activity.data} timeZone={format.timezone} /> : null}
        </div>
      ) : null}
    </RecordDrawer>
  );
}

/**
 * Add a truck, or edit one. The plate is stored uppercase; a plate is unique among live trucks.
 * Staff who manage partners also choose whose it is (P8-42).
 */
function TruckDialog({
  truck,
  canChooseOwner,
  onClose,
}: {
  truck?: Truck;
  canChooseOwner: boolean;
  onClose: () => void;
}) {
  const refresh = useRefreshFleet();
  const [plate, setPlate] = useState(truck?.plate_number ?? "");
  const [capacity, setCapacity] = useState(truck?.capacity_litres.toString() ?? "");
  const [fleetNumber, setFleetNumber] = useState(truck?.fleet_number?.toString() ?? "");
  const [loads, setLoads] = useState(String(truck?.loads_per_day ?? 4));
  const [make, setMake] = useState(truck?.make ?? "");
  const [model, setModel] = useState(truck?.model ?? "");
  const [status, setStatus] = useState<TruckStatus>(truck?.status ?? "active");
  const [owner, setOwner] = useState<OwnerChoice>(truck?.partner_id ?? null);
  const valid =
    plate.trim().length >= 2 &&
    /^\d{3,6}$/.test(capacity) &&
    (fleetNumber === "" || /^\d{1,6}$/.test(fleetNumber)) &&
    /^([1-9]|1\d|20)$/.test(loads);
  const mutation = useMutation({
    mutationFn: () => {
      const body: TruckFields = {
        plate_number: plate.trim(),
        // number-ok: litres, not money
        capacity_litres: Number.parseInt(capacity, 10),
        // number-ok: loads, not money
        loads_per_day: Number.parseInt(loads, 10),
        // number-ok: a fleet number, not money
        fleet_number: fleetNumber ? Number.parseInt(fleetNumber, 10) : undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        status,
        ...(canChooseOwner ? { partner_id: owner } : {}),
      };
      // a field emptied on an edit is removed (null); undefined would keep the old value
      return truck
        ? fleetActions.updateTruck(truck.id, {
            ...body,
            fleet_number: body.fleet_number ?? null,
            make: body.make ?? null,
            model: body.model ?? null,
          })
        : fleetActions.createTruck(body);
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={truck ? `Edit ${truck.plate_number}` : "Add a truck"}>
        <div className={styles.form}>
          <div className={styles.pair}>
            <Input label="Plate" value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="KCA 123A" />
            <Input
              label="Capacity (litres)"
              inputMode="numeric"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="10000"
            />
          </div>
          <Input
            label="Loads a day"
            description="How many loads it can do in a day, 1 to 20. Plan the week fills each truck up to this."
            inputMode="numeric"
            value={loads}
            onChange={(e) => setLoads(e.target.value)}
          />
          <div className={styles.pair}>
            <Input label="Make (optional)" value={make} onChange={(e) => setMake(e.target.value)} />
            <Input label="Model (optional)" value={model} onChange={(e) => setModel(e.target.value)} />
          </div>
          <div className={styles.pair}>
            <Input
              label="Fleet number (optional)"
              inputMode="numeric"
              value={fleetNumber}
              onChange={(e) => setFleetNumber(e.target.value)}
            />
            <Select
              label="Status"
              value={status}
              onValueChange={(v) => setStatus(v as TruckStatus)}
              options={STATUSES.map((s) => ({ value: s, label: TRUCK_STATUS[s][0] }))}
            />
          </div>
          {canChooseOwner ? <OwnerSelect value={owner} onChange={setOwner} /> : null}
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!valid} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              {truck ? "Save" : "Add truck"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RetireTruck({ truck, onClose }: { truck: Truck; onClose: () => void }) {
  const refresh = useRefreshFleet();
  return (
    <ConfirmDialog
      title={`Retire ${truck.plate_number}?`}
      description="It leaves the fleet and Dispatch. Its past deliveries keep it, and its plate can be used again."
      action="Retire"
      keep="Keep it"
      danger
      run={() => fleetActions.retireTruck(truck.id)}
      onDone={refresh}
      onClose={onClose}
    />
  );
}
